import { Grid } from '@material-ui/core';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { EmeraldError } from 'src/common/api/emerald-error';
import {
  restorePage,
  setHomePage,
} from 'src/common/components/emerald-home-page/emerald-home-page';
import { EntryMethod } from 'src/common/enumerations/entry-method';
import { TenderType } from 'src/common/enumerations/tender-type';
import CashierPreferencesHelper from 'src/common/helpers/cashier-preferences-helper';
import { isNumber } from 'src/common/helpers/number-helper';
import { createSpan, endSpan } from 'src/common/helpers/tracing-helper';
import { ITenderAdd } from 'src/common/interfaces/dtos/IPreTenderAdd';
import { ITender } from 'src/common/interfaces/dtos/ITender';
import { IButton } from 'src/common/interfaces/IButton';
import { InputField } from 'src/common/interfaces/InputField';
import { AppState } from 'src/common/redux/configure-store';
import { trainingModeStyles } from 'src/common/training-mode/training-mode-styles';
import TrainingModeWatermark from 'src/common/training-mode/training-mode-watermark';
import { RifmOptions } from 'src/keypad/keypad-operation';
import { popupMessage } from 'src/messages/business-logic/popup-message';
import {
  addValidationMessage,
  createValidationFields,
  getRequiredFieldsMessage,
} from 'src/tendering/business-logic/check-processing-actions';
import { makePaymentAndSetDefaultTender } from 'src/tendering/business-logic/handle-tender-payment';
import { exitTenderingMode } from 'src/tendering/business-logic/tendering-actions';
import KeypadColumn from 'src/tendering/components/keypad-column';
import FormInputView from 'src/tendering/components/process-tender/form-input-view';
import TenderAmountDue from 'src/tendering/components/tender-amount-due';
import TenderGroupTitle from 'src/tendering/components/tender-group-title';
import { styles } from 'src/tendering/components/tender-group-view-styles';
import TenderViewButtons from 'src/tendering/components/tender-view-buttons';
import { TenderParameters } from 'src/tendering/interfaces/tender-parameters';

export const showManualCheckValidation = (props: ManualCheckValidationProps): void => {
  setHomePage(<ManualCheckValidation {...props} />);
};

const isLeftHanded = CashierPreferencesHelper.isLeftHanded();

type Props = ManualCheckValidationProps & ManualCheckValidationStates;

const ManualCheckValidationPage: React.FC<Props> = ({
  defaultValue,
  amountDue,
  selectedTender,
  tenderAdd,
  chequeData,
  trainingModeActive,
  previousPageId,
}: Props): JSX.Element => {
  const maxLength = 12;
  const { t } = useTranslation();
  const classes = styles();
  const trainingModeClasses = trainingModeStyles();

  const [inputDisabled, setInputDisabled] = useState<boolean>(false);

  const [currentAmount, setCurrentAmount] = useState<string>(
    `${isNumber(defaultValue) ? defaultValue?.toFixed(2) : ''}`,
  );

  const missingTenderParameters = useRef<TenderParameters[]>([]);
  const [inputFields, setInputFields] = useState<TenderParameters[]>([]);
  const [keyboardId, setKeyboardId] = useState('');
  const tenderTypeName = selectedTender?.type || tenderAdd?.tender?.tenderType || '';

  const inputCollection = useMemo((): InputField[] => {
    return createValidationFields(inputFields);
  }, [inputFields]);

  const handleOnFocus = (event): void => {
    setKeyboardId(event.target.id);
  };

  const handleInputChange = (event): void => {
    const span = createSpan('Tender View - Amount Entered');
    const sanitizedValidationFieldText = (text: string) => text.replace(/[^0-9]/g, '');
    if (event.target.name === 'current-amount') {
      if (event.target.value.length < maxLength) {
        setCurrentAmount(event.target.value);
      }
    } else {
      const updatedValues: TenderParameters[] = [...inputFields];
      updatedValues[event.target.dataset.id]['value'] = sanitizedValidationFieldText(
        event.target.value,
      );
      updatedValues[event.target.dataset.id]['validationMessage'] = '';
      setInputFields(updatedValues);
    }
    endSpan(span);
  };

  const handleInputPayment = async (): Promise<void> => {
    const span = createSpan(`Tender View - Amount ${currentAmount} submited`);
    setInputDisabled(true);

    const emptyInputFields = inputFields?.filter(field => field.value === '');
    if (emptyInputFields?.length > 0) {
      const emptyInputSpan = createSpan(`Tender View - Amount ${emptyInputFields} submitted`);
      const fieldsWithReqValidationMsg = getRequiredFieldsMessage([...inputFields]);
      setInputFields(fieldsWithReqValidationMsg);
      endSpan(emptyInputSpan);
      return;
    } else {
      setInputFields(inputFields);
    }

    const checkRequiredFields = inputFields.map(inputField => ({
      name: inputField.name,
      value: inputField.value,
    }));

    tenderAdd?.tender?.extendedPaymentParameters?.forEach(item => {
      if (!checkRequiredFields.some(arg => arg.name === item.name)) {
        checkRequiredFields.push({
          name: item.name,
          value: item.value,
        });
      }
    });

    const tenderWithCheckData: ITenderAdd = {
      ...tenderAdd,
      tender: {
        ...tenderAdd?.tender,
        entryMethod: EntryMethod.Manual,
        extendedPaymentParameters: checkRequiredFields,
      },
    };
    await makePaymentAndSetDefaultTender(tenderWithCheckData).catch(popupMessage);
    endSpan(span);
  };

  const submitButton: IButton = {
    label: 'OK',
    onClick: handleInputPayment,
    testId: 'submit-keypad-button',
  };

  const getKeypadHeaderText = (): string => {
    if (tenderTypeName === TenderType.Check) {
      return t('CheckDetails');
    } else {
      return t('ChequeDetails');
    }
  };

  useEffect(() => {
    const isShowingTheFirstTime =
      missingTenderParameters.current === undefined || missingTenderParameters.current.length === 0;
    const requiredFieldsFromBFF: TenderParameters[] = chequeData && chequeData.data.requiredFields;
    if (isShowingTheFirstTime) {
      missingTenderParameters.current = requiredFieldsFromBFF;
      const initialLocalState =
        requiredFieldsFromBFF &&
        [...requiredFieldsFromBFF].map(field => ({
          name: field.name,
          value: field.value || '',
          validationMessage: '',
        }));
      setInputFields(initialLocalState);
    } else {
      setInputFields(currentFieldData => {
        return addValidationMessage(currentFieldData, requiredFieldsFromBFF);
      });
    }
  }, [chequeData, missingTenderParameters]);

  return (
    <Grid
      item
      container
      direction='row'
      justifyContent='space-between'
      className={`fullHeight flexFull ${classes.processTenderContainer} ${
        trainingModeActive ? trainingModeClasses.trainingModeBackground : ''
      }`}
    >
      {trainingModeActive && (
        <TrainingModeWatermark
          dataTestId='process-tender-training-mode-watermark'
          supplementalStyles={trainingModeClasses.fullMiddleAreaTrainingWaterMark}
        />
      )}
      <Grid
        item
        container
        direction='row'
        justifyContent='space-between'
        className={`${classes.processTenderTopRow}`}
      >
        <Grid item xs={8}>
          <TenderGroupTitle title={selectedTender?.description || ''} />
        </Grid>
        <Grid item xs={4}>
          <TenderAmountDue amountDue={amountDue} align='right' />
        </Grid>
      </Grid>

      {!isLeftHanded && (
        <Grid item container direction='row' style={{ paddingBottom: '1vh' }} alignItems='flex-end'>
          <FormColumn
            previousPageId={previousPageId}
            tenderType={tenderTypeName}
            disabled={inputDisabled}
            handleInputPayment={handleInputPayment}
            handleInputChange={handleInputChange}
            handleOnFocus={handleOnFocus}
            inputCollection={inputCollection}
            missingTenderParameters={missingTenderParameters}
            maxLength={maxLength}
          />
          <KeypadColumn
            keypadSubmitButton={submitButton}
            headerText={getKeypadHeaderText()}
            keyboardId={keyboardId}
            isKeypadVisible={true}
            isLeftHanded={isLeftHanded}
            formatter={RifmOptions.price}
          />
        </Grid>
      )}

      {isLeftHanded && (
        <Grid item container direction='row' style={{ paddingBottom: '1vh' }} alignItems='flex-end'>
          <KeypadColumn
            keypadSubmitButton={submitButton}
            headerText={getKeypadHeaderText()}
            keyboardId={keyboardId}
            isKeypadVisible={true}
            isLeftHanded={isLeftHanded}
            formatter={RifmOptions.price}
          />
          <FormColumn
            previousPageId={previousPageId}
            tenderType={tenderTypeName}
            disabled={inputDisabled}
            handleInputPayment={handleInputPayment}
            handleInputChange={handleInputChange}
            handleOnFocus={handleOnFocus}
            inputCollection={inputCollection}
            missingTenderParameters={missingTenderParameters}
          />
        </Grid>
      )}
    </Grid>
  );
};

interface ManualCheckValidationProps {
  defaultValue?: number;
  chequeData?: EmeraldError;
  tenderAdd?: ITenderAdd;
  previousPageId?: string;
}

interface ManualCheckValidationStates {
  amountDue: number;
  trainingModeActive: boolean;
  selectedTender?: ITender;
}
const mapStateToProps = (state: AppState): ManualCheckValidationStates => ({
  amountDue: state.itemList.amountDue,
  trainingModeActive: state.posMode.trainingMode,
  selectedTender: state.tenderMenu.selectedTender,
});

const ManualCheckValidation = connect(mapStateToProps)(ManualCheckValidationPage);
export default ManualCheckValidation;

interface FormColumnProps {
  previousPageId?: string;
  tenderType: string;
  handleInputPayment: () => void;
  handleInputChange: (e) => void;
  handleOnFocus: (e) => void;
  disabled: boolean;
  inputCollection: InputField[];
  missingTenderParameters: React.MutableRefObject<TenderParameters[]>;
  maxLength?: number;
}

const FormColumn = ({
  previousPageId,
  handleInputPayment,
  handleInputChange,
  handleOnFocus,
  disabled,
  inputCollection,
  missingTenderParameters,
  maxLength,
}: FormColumnProps): JSX.Element => {
  const { t } = useTranslation();
  const classes = styles();

  const cancelHandler = (): void => {
    const span = createSpan('Tender View Cancel Button');
    missingTenderParameters.current = [];
    restorePage(previousPageId);
    endSpan(span);
  };

  const exitHandler = async (): Promise<void> => {
    const span = createSpan('Tender View Exit Button');
    await exitTenderingMode();
    endSpan(span);
  };

  return (
    <>
      <Grid
        item
        container
        md={9}
        style={{ alignItems: 'center', justifyContent: 'space-between', alignSelf: 'stretch' }}
        direction='column'
      >
        <FormInputView
          disabled={disabled}
          handleInputPayment={handleInputPayment}
          handleInputChange={handleInputChange}
          handleOnFocus={handleOnFocus}
          inputCollection={inputCollection}
          dataTestId='process-tender'
          containerClass={
            inputCollection && inputCollection.length === 1 ? classes.singleFieldTopMargin : ''
          }
          maxLength={maxLength}
        />

        <Grid item style={{ paddingBottom: '1vh' }}>
          <TenderViewButtons
            secondaryButton={{
              onClick: () => cancelHandler(),
              label: t('Back'),
            }}
            primaryButton={{
              onClick: async () => await exitHandler(),
              label: t('TenderingView_Exit'),
            }}
          />
        </Grid>
      </Grid>
    </>
  );
};
